<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import ChatWelcome from './ChatWelcome.vue';
    import {formatLicensePlate} from '../vehicle/licensePlate';
    import {vehicleTitle} from '../vehicle/vehicleFormat';
    import type {ChatEntry} from '../core/chatTypes';
    import {useChatStore} from '../core/chatStore';
    import ChatRow from './ChatRow.vue';
    import ChatMessage from './ChatMessage.vue';
    import ChatReturnRequest from '../orders/ChatReturnRequest.vue';
    import ChatOrderStatusCard from '../orders/ChatOrderStatusCard.vue';
    import ChatCredentialsForm from '../orders/ChatCredentialsForm.vue';
    import ChatChoiceOptions from './ChatChoiceOptions.vue';
    import ChatProductResults from '../products/ChatProductResults.vue';
    import ChatVehicleCandidates from '../vehicle/ChatVehicleCandidates.vue';
    import ChatComparisonTable from '../products/ChatComparisonTable.vue';

    const messages = ref<HTMLElement | null>(null);
    const chat = useChatStore();
    const {entries, pending, typing, vehicle} = storeToRefs(chat);
    const carLabel = computed(() => (vehicle.value ? vehicleTitle(vehicle.value) : ''));
    const carPlate = computed(() => formatLicensePlate(String(vehicle.value?.licensePlate ?? '')));
    const visibleEntries = computed<ChatEntry[]>(() =>
        typing.value ? [...entries.value, {id: -1, kind: 'typing', stale: true}] : entries.value
    );
    function scrollToEnd(): void {
        const element = messages.value;
        element?.scrollTo({top: element.scrollHeight, behavior: 'instant'});
    }

    function scrollToEntry(entryId: number): void {
        const element = messages.value;
        const entry = element?.querySelector<HTMLElement>(`[data-entry-id="${entryId}"]`);
        if (!element || !entry) return;
        const top =
            element.scrollTop +
            entry.getBoundingClientRect().top -
            element.getBoundingClientRect().top -
            element.clientTop -
            parseFloat(getComputedStyle(element).paddingTop);
        element.scrollTo({top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
    defineExpose({scrollToEnd, scrollToEntry});
</script>

<template>
    <div
        ref="messages"
        class="messages">
        <ChatRow variant="assistant">
            <ChatWelcome
                part="greeting"
                :disabled="pending"
                :car-label="carLabel"
                :car-plate="carPlate" />
        </ChatRow>
        <ChatRow variant="content">
            <ChatWelcome
                part="topics"
                :disabled="pending"
                :car-label="carLabel"
                @ask="chat.send" />
        </ChatRow>

        <ChatRow
            v-for="entry in visibleEntries"
            :key="entry.id"
            :data-entry-id="entry.id"
            :variant="
                entry.kind === 'message' ? (entry.tone === 'me' ? 'me' : entry.tone === 'error' ? 'content' : 'assistant') : entry.kind === 'typing' ? 'assistant' : 'content'
            ">
            <ChatMessage
                v-if="entry.kind === 'message'"
                :label="entry.label"
                :text="entry.text"
                :tone="entry.tone" />

            <ChatProductResults
                v-else-if="entry.kind === 'products'"
                :products="entry.products"
                :meta="entry.meta"
                :disabled="entry.stale"
                @compare="chat.compare" />

            <ChatComparisonTable
                v-else-if="entry.kind === 'comparison'"
                :table="entry.table" />

            <ChatVehicleCandidates
                v-else-if="entry.kind === 'vehicleCandidates'"
                :candidates="entry.candidates"
                :disabled="entry.stale"
                @select="chat.chooseVehicleCandidate" />

            <ChatChoiceOptions
                v-else-if="entry.kind === 'choices'"
                :labels="entry.labels"
                :counts="entry.counts"
                :logos="entry.logos"
                :choice-ids="entry.choiceIds"
                :disabled="entry.stale"
                @choose="chat.choose" />

            <ChatOrderStatusCard
                v-else-if="entry.kind === 'orderStatus'"
                :status="entry.status" />

            <ChatCredentialsForm
                v-else-if="entry.kind === 'orderVerification'"
                :disabled="entry.stale"
                @verify="chat.verifyOrderStatus" />

            <ChatCredentialsForm
                v-else-if="entry.kind === 'returnVerification'"
                :disabled="entry.stale"
                @verify="chat.verifyReturn" />

            <ChatReturnRequest
                v-else-if="entry.kind === 'returnRequest'"
                :eligibility="entry.eligibility"
                :disabled="entry.stale"
                @submit="chat.submitReturn"
                @cancel="chat.cancelReturn" />

            <div
                v-else-if="entry.kind === 'typing'"
                class="chat-bubble chat-bubble--ai typing">
                <span class="typing__dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </span>
            </div>
        </ChatRow>

        <!--
            Two things the design draws that still have no data behind them. Each needs a field on
            the turn payload before it can be an entry kind here - never a guess from the message
            text, which is exactly the proxy CLAUDE.md section 4.3 rules out.

            The hand-off card (artboard "6 · Doorverbinden"): agent availability, wait time, a
               live-chat hand-off, and the case summary sent along with it. Nothing in this chat
               talks to a human agent. The phone number and mail address do exist server-side -
               ReturnEligibility carries customerServicePhone/customerServiceMail for the
               not-eligible case, and SiteInfo/siteSettings hold both - but toCard() drops them,
               so even a contact-only version of this card needs them added to the payload first.
        -->
    </div>
</template>

<style scoped lang="scss">
    @import '../../../sass/globals';
    /* position: relative makes the scroller the containing block of what's absolutely positioned
       inside it (the .sr-only labels): without it they escaped its clipping, made the modal body
       thousands of pixels tall, and focusing the composer scrolled that overflow:hidden body - the
       whole panel slid up, header off-screen, an empty band under the composer. */
    .messages {
        position: relative;
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        gap: 16px;
        min-height: 0;
        padding: var(--chat-pad);
        overflow-y: auto;
        scrollbar-width: thin;

        border-bottom: 1px solid #e4e4e4;
    }

    /* Short conversations sit at the bottom of the panel, the way the design shows them, without
       giving up normal scrolling once the transcript outgrows the panel. */
    .messages > :first-child {
        margin-top: auto;
    }

    .messages::-webkit-scrollbar {
        width: 8px;
    }

    .messages::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: $grey;
    }

    .typing {
        display: inline-flex;
        align-items: center;
        padding: 15px 16px;
    }

    .typing__dots {
        display: inline-flex;
        gap: 4px;
    }

    .typing__dots span {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: $black;
        opacity: 0.3;
        animation: chat-typing 1s infinite ease-in-out;
    }

    .typing__dots span:nth-child(2) {
        animation-delay: 0.15s;
    }

    .typing__dots span:nth-child(3) {
        animation-delay: 0.3s;
    }

    @media (max-width: $chat-mobile) {
        .messages {
            gap: 12px;
        }
    }

    @keyframes chat-typing {
        0%,
        100% {
            opacity: 0.3;
            transform: translateY(0);
        }
        50% {
            opacity: 1;
            transform: translateY(-1px);
        }
    }
</style>
